import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Stethoscope, ArrowRight, Heart, Activity, Sun, Leaf, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

const CATEGORY_META = {
    autocuidado: { label: "Autocuidado", Icon: Heart, color: "text-[#C85A47]", bg: "bg-[#FBEEE6]" },
    cuidados_idoso: { label: "Cuidados com a pessoa idosa", Icon: Heart, color: "text-[#2A5746]", bg: "bg-[#DAE3D5]" },
    longevidade: { label: "Longevidade", Icon: Activity, color: "text-[#556B2F]", bg: "bg-[#EDF3E8]" },
    estilo_vida: { label: "Medicina do estilo de vida", Icon: Leaf, color: "text-[#4A6B53]", bg: "bg-[#EDEEE6]" },
    espiritualidade: { label: "Espiritualidade", Icon: Sun, color: "text-[#9C6E28]", bg: "bg-[#FBF4E5]" },
};

export default function BlogPage() {
    const [posts, setPosts] = useState([]);
    const [category, setCategory] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const params = category ? { category } : {};
                const { data } = await publicApi.get("/blog/posts", { params });
                setPosts(data);
            } finally { setLoading(false); }
        })();
    }, [category]);

    return (
        <div className="min-h-screen bg-base">
            <PublicHeader />

            <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-10">
                <div className="mb-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-alt text-brand text-xs font-semibold uppercase tracking-widest mb-4">
                        <Sparkles size={12} /> Comunicação em saúde
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-brand tracking-tight leading-[1.05]">
                        Minuto da Saúde
                    </h1>
                    <p className="mt-4 text-lg text-[var(--text-secondary)] max-w-2xl">
                        Conteúdo cuidadosamente selecionado sobre autocuidado, longevidade, medicina do estilo de vida e espiritualidade — com o olhar do Dr. Marcos Rodrigo Fernandes.
                    </p>
                </div>

                <div className="flex flex-wrap gap-2 mb-8">
                    <button
                        onClick={() => setCategory("")}
                        data-testid="blog-cat-all"
                        className={`text-sm px-3 py-1.5 rounded-full ${!category ? "bg-brand text-white" : "bg-alt text-brand hover:bg-[#EAE4D8]"}`}
                    >Todas</button>
                    {Object.entries(CATEGORY_META).map(([k, m]) => (
                        <button
                            key={k}
                            onClick={() => setCategory(k)}
                            data-testid={`blog-cat-${k}`}
                            className={`text-sm px-3 py-1.5 rounded-full ${category === k ? "bg-brand text-white" : "bg-alt text-brand hover:bg-[#EAE4D8]"}`}
                        >{m.label}</button>
                    ))}
                </div>

                {loading ? <p className="text-muted-clinical">Carregando…</p> : posts.length === 0 ? (
                    <div className="card-earthy text-sm text-muted-clinical">Nenhum artigo publicado nesta categoria ainda.</div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {posts.map((p) => {
                            const meta = CATEGORY_META[p.category] || CATEGORY_META.autocuidado;
                            const Icon = meta.Icon;
                            return (
                                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-post-${p.slug}`} className="card-earthy hover:shadow-md transition-shadow block overflow-hidden p-0">
                                    {p.cover_image_url ? (
                                        <div className="aspect-[16/9] bg-alt overflow-hidden">
                                            <img src={p.cover_image_url.startsWith('/api') ? `${BACKEND_URL}${p.cover_image_url}` : p.cover_image_url} alt={p.title} className="w-full h-full object-cover" />
                                        </div>
                                    ) : (
                                        <div className={`aspect-[16/9] ${meta.bg} flex items-center justify-center`}>
                                            <Icon size={48} className={meta.color} />
                                        </div>
                                    )}
                                    <div className="p-5">
                                        <div className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider ${meta.color} mb-2`}>
                                            <Icon size={11} /> {meta.label}
                                        </div>
                                        <h3 className="font-display text-lg font-medium text-brand leading-snug mb-2">{p.title}</h3>
                                        {p.excerpt && <p className="text-sm text-[var(--text-secondary)]">{p.excerpt}</p>}
                                        <div className="flex items-center gap-1 text-xs text-brand-secondary mt-3">
                                            Ler artigo <ArrowRight size={12} />
                                        </div>
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </section>

            <PublicFooter />
        </div>
    );
}

export function PublicHeader() {
    return (
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-[rgba(30,63,51,0.1)]">
            <div className="max-w-6xl mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
                <Link to="/" className="flex items-center gap-3 text-brand font-display font-semibold">
                    <span className="w-9 h-9 rounded-xl bg-brand text-white flex items-center justify-center"><Stethoscope size={18} /></span>
                    Clínica Fernandes
                </Link>
                <div className="flex items-center gap-2 text-sm">
                    <Link to="/blog" className="btn-ghost">Minuto da Saúde</Link>
                    <Link to="/depoimentos" className="btn-ghost hidden md:inline-flex">Depoimentos</Link>
                    <Link to="/login" className="btn-primary">Entrar</Link>
                </div>
            </div>
        </header>
    );
}

export function PublicFooter() {
    return (
        <footer className="border-t border-[rgba(30,63,51,0.08)] py-8 mt-10">
            <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-sm text-muted-clinical">
                <div>
                    <div className="font-display text-brand font-medium">Clínica de Assistência à Pessoa Idosa</div>
                    <div>Dr. Marcos Rodrigo Fernandes — Geriatra</div>
                </div>
                <div>Cuidado centrado na pessoa · LGPD · © {new Date().getFullYear()}</div>
            </div>
        </footer>
    );
}

export { CATEGORY_META };
